import React, { useState } from "react";
import { Create, useAutocomplete } from "@refinedev/mui";
import {
  Box,
  TextField,
  Autocomplete,
  FormControl,
  FormLabel,
  Slider,
  Typography,
  Grid,
  Card,
  CardContent,
  InputAdornment,
} from "@mui/material";
import { useForm } from "@refinedev/react-hook-form";
import { Controller } from "react-hook-form";
import { IResourceComponentsProps, useTranslate, useList } from "@refinedev/core";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import EmojiPicker from "emoji-picker-react";
import { useAuth } from "../../contexts/auth-context";
import AddressAutocomplete, { GeoapifyResult } from "../../components/forms/AddressAutocomplete";
import ZoneTypeSelector, { ZoneType } from "../../components/forms/ZoneTypeSelector";
import ShapeTypeSelector, { ShapeType } from "../../components/forms/ShapeTypeSelector";
import PolygonDrawMap, { PolygonCoordinate } from "../../components/forms/PolygonDrawMap";
import { useSubGroups } from "../../hooks/useSubGroups";

interface PlaceFormValues {
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  radius: number;
  emoji: string;
  zoneType: ZoneType;
  shapeType: ShapeType;
  polygon: PolygonCoordinate[] | null;
  entrepriseId?: number;
  subGroupId?: number;
}

// Fix pour les icônes Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

export const SharedPlacesCreate: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "ADMIN";

  const {
    saveButtonProps,
    refineCore: { formLoading },
    register,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<PlaceFormValues>({
    refineCoreProps: {
      resource: "places",
    },
    defaultValues: {
      radius: 100,
    },
  });

  const [position, setPosition] = useState<[number, number]>([48.8566, 2.3522]); // Paris par défaut
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [zoneType, setZoneType] = useState<ZoneType>("AUTHORIZED");
  const [shapeType, setShapeType] = useState<ShapeType>("CIRCLE");
  const [polygon, setPolygon] = useState<PolygonCoordinate[]>([]);

  const radius = watch("radius") || 100;
  const selectedEmoji = watch("emoji") || "📍";
  const selectedEntrepriseId =
    (watch("entrepriseId") as number | undefined) ?? currentUser?.entrepriseId;

  const { subGroups, loading: subGroupsLoading } = useSubGroups(
    selectedEntrepriseId ? Number(selectedEntrepriseId) : undefined,
    { enabled: !!selectedEntrepriseId },
  );

  // Fetch company places to center the map on them
  const { data: placesData } = useList({
    resource: "places",
    filters: currentUser?.entrepriseId ? [
      {
        field: "entrepriseId",
        operator: "eq",
        value: currentUser.entrepriseId,
      },
    ] : [],
    pagination: {
      pageSize: 100,
    },
  });

  // Calculate center from company places
  React.useEffect(() => {
    if (placesData?.data && placesData.data.length > 0) {
      const validPlaces = placesData.data.filter(
        (place: any) => place.latitude && place.longitude
      );

      if (validPlaces.length > 0) {
        // Calculate average position
        const avgLat = validPlaces.reduce((sum: number, place: any) => sum + place.latitude, 0) / validPlaces.length;
        const avgLon = validPlaces.reduce((sum: number, place: any) => sum + place.longitude, 0) / validPlaces.length;
        setPosition([avgLat, avgLon]);
      }
    }
  }, [placesData]);

  // Autocomplete pour les entreprises (seulement pour ADMIN)
  const { autocompleteProps: companyAutocompleteProps } = useAutocomplete({
    resource: "company",
  });

  React.useEffect(() => {
    setValue("latitude", position[0]);
    setValue("longitude", position[1]);
  }, [position, setValue]);

  // Si l'utilisateur n'est pas admin, on définit automatiquement son entrepriseId
  React.useEffect(() => {
    if (!isAdmin && currentUser?.entrepriseId) {
      setValue("entrepriseId", currentUser.entrepriseId);
    }
  }, [isAdmin, currentUser, setValue]);

  // Sync zone type and shape type with form
  React.useEffect(() => {
    setValue("zoneType", zoneType);
  }, [zoneType, setValue]);

  React.useEffect(() => {
    setValue("shapeType", shapeType);
  }, [shapeType, setValue]);

  React.useEffect(() => {
    setValue("polygon", polygon.length >= 3 ? polygon : null);
  }, [polygon, setValue]);

  // Reset polygon when switching to circle shape
  React.useEffect(() => {
    if (shapeType === "CIRCLE") {
      setPolygon([]);
    }
  }, [shapeType]);

  // Reset shape type when switching to authorized zone
  React.useEffect(() => {
    if (zoneType === "AUTHORIZED") {
      setShapeType("CIRCLE");
    }
  }, [zoneType]);

  return (
    <Create isLoading={formLoading} saveButtonProps={saveButtonProps}>
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <TextField
                  {...register("name", {
                    required: translate("sharedPlaces.form.nameRequired"),
                  })}
                  error={!!(errors as any)?.name}
                  helperText={(errors as any)?.name?.message}
                  margin="normal"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  type="text"
                  label={translate("sharedPlaces.fields.name")}
                  name="name"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                          sx={{
                            fontSize: "2rem",
                            cursor: "pointer",
                            padding: "4px",
                            borderRadius: "4px",
                            "&:hover": {
                              backgroundColor: "rgba(25, 118, 210, 0.08)",
                            },
                          }}
                        >
                          {selectedEmoji}
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                />

            {showEmojiPicker && (
              <Box sx={{ mt: 2 }}>
                <EmojiPicker
                  onEmojiClick={(emojiData: any) => {
                    setValue("emoji", emojiData.emoji);
                    setShowEmojiPicker(false);
                  }}
                />
              </Box>
            )}

            {/* Zone Type Selector */}
            <ZoneTypeSelector
              value={zoneType}
              onChange={setZoneType}
            />

            {/* Shape Type Selector - only visible for forbidden zones */}
            {zoneType === "FORBIDDEN" && (
              <ShapeTypeSelector
                value={shapeType}
                onChange={setShapeType}
              />
            )}

            {/* Radius Slider - only visible for circle shapes */}
            {shapeType === "CIRCLE" && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <FormLabel>{translate("sharedPlaces.form.notificationRadius")}</FormLabel>
                <Slider
                  value={radius}
                  onChange={(_, value) => setValue("radius", value as number)}
                  min={50}
                  max={1000}
                  step={50}
                  valueLabelDisplay="auto"
                  marks={[
                    { value: 50, label: "50m" },
                    { value: 500, label: "500m" },
                    { value: 1000, label: "1km" },
                  ]}
                />
              </Box>
            )}

            {isAdmin && (
              <Controller
                control={control}
                name="entrepriseId"
                render={({ field }) => (
                  <FormControl fullWidth margin="normal">
                    <Autocomplete
                      {...companyAutocompleteProps}
                      {...field}
                      onChange={(_, value: any) => {
                        field.onChange(value?.id);
                      }}
                      getOptionLabel={(item: any) => {
                        return item.name ? item.name : "";
                      }}
                      isOptionEqualToValue={(option, value) =>
                        value === undefined || option?.id?.toString() === value?.id?.toString()
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={translate("sharedPlaces.form.companyOptional")}
                          variant="outlined"
                          helperText={translate("sharedPlaces.form.companyHelperText")}
                        />
                      )}
                    />
                  </FormControl>
                )}
              />
            )}

            {!!selectedEntrepriseId && (
              <Controller
                control={control}
                name="subGroupId"
                render={({ field }) => (
                  <FormControl fullWidth margin="normal">
                    <Autocomplete
                      options={subGroups}
                      loading={subGroupsLoading}
                      value={subGroups.find((sg) => sg.id === field.value) || null}
                      onChange={(_, value) => field.onChange(value?.id)}
                      getOptionLabel={(item) => item?.name || ""}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={translate("sharedPlaces.form.subGroup", "Sous-groupe (optionnel)")}
                          placeholder={translate("sharedPlaces.form.selectSubGroup", "Sélectionner un sous-groupe")}
                        />
                      )}
                    />
                  </FormControl>
                )}
              />
            )}

                {!isAdmin && currentUser?.entrepriseId && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: "info.light", borderRadius: 1 }}>
                    <Typography variant="body2">
                      {translate("sharedPlaces.form.autoSharedWithCompany")}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ mt: 2 }}>
                  <AddressAutocomplete
                    label={translate("sharedPlaces.form.searchAddress")}
                    placeholder={translate("sharedPlaces.form.enterAddress")}
                    onAddressSelect={(result: GeoapifyResult) => {
                      setPosition([result.lat, result.lon]);
                      setValue("latitude", result.lat);
                      setValue("longitude", result.lon);
                      setValue("address", result.formatted || result.address_line1 || `${result.lat}, ${result.lon}`);
                    }}
                  />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <PolygonDrawMap
                    position={position}
                    setPosition={setPosition}
                    radius={radius}
                    shapeType={shapeType}
                    zoneType={zoneType}
                    polygon={polygon}
                    onPolygonChange={setPolygon}
                    height="500px"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <input type="hidden" {...register("latitude")} />
        <input type="hidden" {...register("longitude")} />
        <input type="hidden" {...register("address")} />
        <input type="hidden" {...register("emoji")} />
        <input type="hidden" {...register("radius")} />
        <input type="hidden" {...register("zoneType")} />
        <input type="hidden" {...register("shapeType")} />
        <input type="hidden" {...register("polygon")} />
        {!isAdmin && <input type="hidden" {...register("entrepriseId")} />}
      </Box>
    </Create>
  );
};
